/**
 * Article shoppable cards — option availability enforcement and submit guard.
 *
 * Article-scoped. No shared theme file is modified; this reads the variant JSON the shared
 * picker already emits (`snippets/variant-picker.liquid`) and enforces the article contract on
 * top of it.
 *
 * 1. Availability, UNCONDITIONALLY. The picker only writes its `.is-unavailable` classes when
 *    `enable_dynamic_availability` is on, and it never sets `disabled` on the visible radios in
 *    any case. Articles require sold-out sizes to be genuinely non-selectable regardless of that
 *    setting, so availability is computed here from the embedded variant data and mirrored onto
 *    `disabled`. The picker's classes are honoured as a second signal when present.
 *
 * 2. Submit guard. `ProductForm.validate()` (`assets/main.js:2829-2835`) validates only the FIRST
 *    radio of each option group, and a disabled control is barred from constraint validation — so
 *    disabling a sold-out first size would otherwise let the group report valid with nothing
 *    chosen, and Add to cart could submit an empty variant id. The guard runs in the CAPTURE
 *    phase on the card root, ahead of ProductForm's bubble-phase handler on the form, and:
 *      - validates the first ENABLED radio per group, reusing the browser's own localised message;
 *      - requires a resolved variant id;
 *      - requires that variant to be AVAILABLE in the embedded data;
 *      - respects the add button's native `disabled` state, which ProductForm itself does not
 *        check (it reads `aria-disabled`, `main.js:2708-2715`).
 *
 *    This guards the submit EVENT. Native `form.submit()` bypasses submit listeners entirely and
 *    is outside what any event listener can promise.
 *
 * Registered as a custom element so it upgrades on direct load, PJAX arrival and theme-editor
 * section reload without listening for lifecycle events.
 */
if (!customElements.get('article-option-sync')) {
  class ArticleOptionSync extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.js-product');
      if (!this.card) return;

      this.refresh();

      // The picker writes its classes from its own constructor and again on every change;
      // upgrade order between the two elements is not guaranteed, so observe rather than assume.
      if (!this.observer) {
        this.observer = new MutationObserver(() => this.refresh());
      }
      this.observer.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'checked']
      });

      if (!this.boundRefresh) this.boundRefresh = this.refresh.bind(this);
      if (!this.boundGuard) this.boundGuard = this.guardSubmit.bind(this);
      this.addEventListener('on:variant:change', this.boundRefresh);
      this.addEventListener('change', this.boundRefresh);
      this.card.addEventListener('submit', this.boundGuard, true);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.removeEventListener('on:variant:change', this.boundRefresh);
      this.removeEventListener('change', this.boundRefresh);
      if (this.card && this.boundGuard) {
        this.card.removeEventListener('submit', this.boundGuard, true);
      }
    }

    /** @returns {object|null} The picker's embedded variant payload, or null. */
    variantData() {
      if (this.data === undefined) {
        const el = this.querySelector('[type="application/json"]');
        try {
          this.data = el ? JSON.parse(el.textContent) : null;
        } catch (err) {
          this.data = null;
        }
      }
      return this.data;
    }

    /** @returns {HTMLElement[]} The listed option groups, in product-option order. */
    groups() {
      return Array.from(this.querySelectorAll('.option-selector')).filter(
        (group) => group.dataset.selectorType === 'listed'
      );
    }

    /**
     * Recomputes which option values are selectable and mirrors that onto `disabled`.
     * A value is selectable when at least one AVAILABLE variant matches it together with the
     * values chosen in the groups BEFORE it. Unset and later groups do not constrain the match.
     */
    refresh() {
      if (this.refreshing) return;
      this.refreshing = true;

      const data = this.variantData();
      const variants = data && Array.isArray(data.variants) ? data.variants : null;
      const groups = this.groups();
      const selected = groups.map((group) => {
        const checked = group.querySelector('input.js-option:checked');
        return checked ? checked.value : null;
      });

      groups.forEach((group, groupIndex) => {
        group.querySelectorAll('input.js-option').forEach((input) => {
          let unavailable = input.classList.contains('is-unavailable')
            || input.classList.contains('is-nonexistent');

          if (variants) {
            // 'down' cascade, matching the dynamic_availability_mode the card passes to the
            // picker: a group is constrained only by the groups BEFORE it. Constraining it by
            // later groups too would make an earlier option unselectable as soon as a later one
            // was chosen — e.g. Colour could not be changed once a Size was picked.
            const candidate = selected.slice();
            for (let i = groupIndex; i < candidate.length; i += 1) candidate[i] = null;
            candidate[groupIndex] = input.value;
            const matchable = variants.some((variant) => variant.available
              && candidate.every((value, i) => value === null || variant.options[i] === value));
            if (!matchable) unavailable = true;
          }

          ArticleOptionSync.setUnavailable(input, unavailable);
        });
      });

      this.refreshing = false;
    }

    /**
     * Blocks submission unless every listed group has an enabled checked radio, the add button is
     * not natively disabled, and the resolved variant exists and is available.
     * @param {Event} evt - The form's submit event.
     */
    guardSubmit(evt) {
      let valid = true;

      this.groups().forEach((group) => {
        const inputs = Array.from(group.querySelectorAll('input.js-option'));
        if (inputs.length === 0) return;
        if (inputs.some((input) => input.checked && !input.disabled)) return;

        valid = false;
        // Validate the first ENABLED radio — validating a disabled one always reports true,
        // which is the defect this guard exists to close.
        const target = inputs.find((input) => !input.disabled);
        if (target && typeof target.reportValidity === 'function') target.reportValidity();
      });

      const addBtn = this.card.querySelector('.js-product-form [name="add"]');
      if (addBtn && addBtn.disabled) valid = false;

      const idInput = this.card.querySelector('.js-product-form [name="id"]');
      const id = idInput ? idInput.value : '';
      if (!id) {
        valid = false;
      } else {
        const data = this.variantData();
        if (data && Array.isArray(data.variants)) {
          const variant = data.variants.find((v) => String(v.id) === String(id));
          if (!variant || !variant.available) valid = false;
        }
      }

      if (!valid) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }
    }

    /**
     * @param {HTMLInputElement} input - A visible option radio rendered by variant-picker.
     * @param {boolean} unavailable - Whether the value is currently unselectable.
     */
    static setUnavailable(input, unavailable) {
      // A disabled radio leaves the tab order, which is intended: an unavailable size should not
      // be reachable by keyboard either.
      if (input.disabled !== unavailable) input.disabled = unavailable;

      const label = input.nextElementSibling;
      if (label && label.classList) {
        label.classList.toggle('article-shop-card__opt--unavailable', unavailable);
      }
    }
  }

  customElements.define('article-option-sync', ArticleOptionSync);
}
